import classes from './Kommunikation.module.css';

import {
  Alert,
  Button,
  FlexSpacer,
  Typography,
  Skeleton,
  Stack,
  Title,
  Divider,
  Card
} from '@efast_public/fjd-component-library';
import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import NachrichtItem from './Nachrichten/NachrichtItem';
import {
  NachrichtModel,
  VersandStatus
} from '../../../../api/models/Nachrichten/NachrichtModel';
import NachrichtContent from './Nachrichten/NachrichtContent';
import { ContentTab } from '../../SchreibtischPage';
import NachrichtSendenModal from '../../../../modals/NachrichtSenden/NachrichtSendenModal';
import { Bearbeitungsstand } from '../../../../api/models/Status/Bearbeitungsstand';
import { useMutation, useQuery } from '@tanstack/react-query';
import { CaseDetailsModel } from '../../../../api/models/Case/CaseDetailsModel';
import { AxiosError } from 'axios';
import { BehApiRoutes, behClient } from '../../../../api/beh-api';
import { useNotification } from '../../../../shared/notification/useNotification';
import { useProfile } from '../../../../shared/hooks/AuthContext';
import nachrichtItem from './Nachrichten/NachrichtItem';

const TERMINAL_VERSAND_STATUSES = new Set([
  VersandStatus.SUCCESSFUL,
  VersandStatus.FAILED,
  VersandStatus.CANCELED
]);

const VERSAND_POLL_INTERVAL_MS = 3000;

interface KommunikationProps {
  readonly setContentTabState: (val: ContentTab) => void;
  readonly openAbschliessenModal: () => void;
}

const Kommunikation = (props: KommunikationProps) => {
  const { caseId } = useParams() as { caseId: string };
  const { notifyUserFromRequest } = useNotification();
  const { data: caseDetails, refetch: refetchCaseDetails } = useQuery<
    CaseDetailsModel,
    AxiosError
  >({
    queryKey: [BehApiRoutes.case(caseId ?? '')]
  });
  const {
    data: nachrichten,
    error: useSubmissionsAsNachrichtenError,
    isLoading,
    refetch: refetchNachrichten
  } = useQuery<Array<NachrichtModel>, AxiosError>({
    queryKey: [
      BehApiRoutes.caseSubmissions(caseId ?? ''),
      'application/vnd.fjd.beh.message+json'
    ],
    queryFn: () =>
      notifyUserFromRequest(() =>
        behClient.get(BehApiRoutes.caseSubmissions(caseId ?? ''), {
          headers: { accept: 'application/vnd.fjd.beh.message+json' }
        })
      ),
    refetchInterval: (query) => {
      const data = query.state.data as NachrichtModel[] | undefined;
      const hasActiveVersand = data?.some(
        (n) =>
          n.versandStatus &&
          !TERMINAL_VERSAND_STATUSES.has(n.versandStatus.status)
      );
      return hasActiveVersand ? VERSAND_POLL_INTERVAL_MS : false;
    }
  });

  const { mutateAsync: markMessageAsRead } = useMutation({
    mutationFn: ({
      submissionId,
      userId,
      caseId
    }: {
      submissionId: string;
      userId: string;
      caseId: string;
    }) =>
      notifyUserFromRequest(
        () =>
          behClient.put(BehApiRoutes.messageRead(caseId, submissionId, userId)),
        {
          errorMessage: 'Die Nachricht konnte nicht gelesen werden.'
        }
      )
  });
  const [selectedNachricht, setSelectedNachricht] = useState<
    NachrichtModel | undefined
  >(undefined);
  const [openSendenModal, setOpenSendenModal] = useState(false);
  const canCommunicate = caseDetails?.canCommunicate;
  const canInteract =
    caseDetails?.bearbeitungsstand !== Bearbeitungsstand.ABGESCHLOSSEN;
  const alertTitle =
    canCommunicate && canInteract
      ? 'Wählen Sie auf der linken Seite eine Nachricht aus oder verfassen Sie eine.'
      : 'Wählen Sie auf der linken Seite eine Nachricht aus.';
  const initialSubmissionAbsender = caseDetails?.initialSubmission.absenderName;
  const { profile } = useProfile();
  const bearbeitendeNutzer = caseDetails?.bearbeiterDto?.id === profile?.id;
  const isLoggedInUserBearbeiter =
    caseDetails?.bearbeiterDto?.id === profile?.id;

  const onClickNachricht = (nachricht: NachrichtModel) => {
    if (nachricht.submissionId === selectedNachricht?.submissionId) {
      setSelectedNachricht(undefined);
    } else {
      setSelectedNachricht(nachricht);
    }
    const currentBearbeiterId = caseDetails?.bearbeiterDto?.id;
    if (isLoggedInUserBearbeiter && !nachricht.hasBeenRead) {
      markMessageAsRead({
        submissionId: nachricht.submissionId,
        userId: currentBearbeiterId ?? '',
        caseId
      }).then(() => refetchNachrichten());
    }
  };

  return (
    <Stack direction={'column'} spacing={'xs'}>
      <Stack
        className={classes.caseNameTitle}
        id={'nachrichten_liste'}
        direction={'row'}
      >
        <Title level={2}>Nachrichten</Title>
        <FlexSpacer />
        {canInteract && (
          <Button
            disabled={!canCommunicate || !bearbeitendeNutzer}
            variant={'primary'}
            size={'md'}
            beforeIcon={'add'}
            onClick={() => setOpenSendenModal(!openSendenModal)}
            id={'nachrichten_schreiben_btn'}
          >
            Nachricht schreiben
          </Button>
        )}
      </Stack>
      <Card id={'container'} className={classes.container}>
        <div id={'left'} className={classes.left}>
          {isLoading && (
            <Stack direction={'column'} spacing={'s'}>
              {['1', '2'].map((item) => (
                <Skeleton key={item} height={'80px'} width="300px" />
              ))}
            </Stack>
          )}
          <Stack
            id={'nachrichten_liste'}
            className={classes.nachrichten}
            direction={'column'}
          >
            {caseDetails &&
              nachrichten?.map((value) => (
                <NachrichtItem
                  caseDetails={caseDetails}
                  isSelected={
                    value.submissionId === selectedNachricht?.submissionId
                  }
                  key={Math.random()}
                  nachricht={value}
                  selectNachrichtHandler={onClickNachricht}
                  openAbschliessenModal={props.openAbschliessenModal}
                />
              ))}
          </Stack>
        </div>
        <div id={'right'} className={classes.right}>
          {selectedNachricht && caseDetails ? (
            <NachrichtContent
              caseDetails={caseDetails}
              nachricht={selectedNachricht}
              setContentTabState={props.setContentTabState}
              antragssteller={initialSubmissionAbsender ?? undefined}
              bescheidstatus={caseDetails?.bescheidstatus}
            />
          ) : (
            <div id={'nachricht_verfassen_hinweis'}>
              <Alert variant="info" message={alertTitle}>
                {canCommunicate && canInteract && bearbeitendeNutzer && (
                  <Button
                    onClick={() => setOpenSendenModal(!openSendenModal)}
                    size="sm"
                    variant="secondary-outline"
                  >
                    neue Nachricht
                  </Button>
                )}
              </Alert>
            </div>
          )}
        </div>
        {caseDetails && openSendenModal && (
          <NachrichtSendenModal
            caseDetails={caseDetails}
            onClose={() => {
              setOpenSendenModal(false);
              refetchCaseDetails();
            }}
            caseId={caseId}
            caseVersion={caseDetails.version}
            useSubmissionsAsNachrichtenError={useSubmissionsAsNachrichtenError}
            isOpen
          />
        )}
      </Card>
    </Stack>
  );
};

export default Kommunikation;
